/**
 * URLからサイトタイトルを取得する（自前API優先）
 * @param {string} url サイトのURL
 * @returns {Promise<string>} サイトのタイトル
 */

// 自前APIで取得したファビコンURLのキャッシュ
let _cachedFavicon = null;
let _faviconAlreadyFetched = false;

export async function fetchSiteTitle(url) {
  console.log(`[fetchSiteTitle] タイトル取得開始: ${url}`);
  _cachedFavicon = null;
  _faviconAlreadyFetched = false;
  
  try {
    // 自前API（Pages Function）を試行
    const apiUrl = `/api/get-title?url=${encodeURIComponent(url)}`;
    console.log(`[fetchSiteTitle] 自前API試行: ${apiUrl}`);
    
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`API エラー: ${response.status} ${response.statusText}`);
    }
    
    const text = await response.text();
    
    try {
      const data = JSON.parse(text);
      // ファビコンURLをキャッシュ（getFaviconUrlWithFallbackで使用）
      if (data && data.favicon) {
        _cachedFavicon = data.favicon;
        console.log(`[fetchSiteTitle] ファビコンURLキャッシュ: ${_cachedFavicon}`);
      }
      _faviconAlreadyFetched = true;
      if (data && data.title && data.title.trim()) {
        const title = data.title.trim();
        console.log(`[fetchSiteTitle] 自前API成功: ${title}`);
        return title;
      } else {
        throw new Error('APIからタイトルを取得できませんでした');
      }
    } catch (parseError) {
      console.log(`[fetchSiteTitle] JSON解析エラー: ${parseError.message}`);
      throw new Error('API応答の解析に失敗しました');
    }
    
  } catch (error) {
    _faviconAlreadyFetched = true;
    // CORSエラーの場合は詳細なログを控える
    if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
      console.log(`[fetchSiteTitle] API接続制限により自動取得できませんでした: ${url}`);
    } else {
      console.log(`[fetchSiteTitle] 自前API失敗: ${error.message}`);
    }
    
    // フォールバック: URLからドメインベース名前生成
    const generatedName = generateNameFromUrl(url);
    console.log(`[fetchSiteTitle] フォールバック名前生成: ${generatedName}`);
    return generatedName;
  }
}

/**
 * URLからドメインベースの名前を生成する
 * @param {string} url サイトのURL
 * @returns {string} ドメインベースの名前
 */
function generateNameFromUrl(url) {
  try {
    const urlObj = new URL(url);
    let hostname = urlObj.hostname;
    
    // www. プレフィックスを除去
    hostname = hostname.replace(/^www\./, '');
    
    // 一般的なサービスの場合は美しい名前に変換
    const domainMappings = {
      'google.com': 'Google',
      'youtube.com': 'YouTube', 
      'github.com': 'GitHub',
      'stackoverflow.com': 'Stack Overflow',
      'amazon.co.jp': 'Amazon',
      'amazon.com': 'Amazon',
      'twitter.com': 'Twitter',
      'facebook.com': 'Facebook',
      'instagram.com': 'Instagram',
      'linkedin.com': 'LinkedIn',
      'qiita.com': 'Qiita',
      'zenn.dev': 'Zenn',
      'memoqr.txt365.org': 'MemoQR',
      'txt365.org': 'TXT365',
      'note.com': 'note',
      'wikipedia.org': 'Wikipedia',
      'reddit.com': 'Reddit',
      'medium.com': 'Medium',
      'dev.to': 'DEV Community',
      'hatena.ne.jp': 'はてな',
      'yahoo.co.jp': 'Yahoo! JAPAN',
      'yahoo.com': 'Yahoo!',
      'bing.com': 'Bing',
      'duckduckgo.com': 'DuckDuckGo',
      'npmjs.com': 'npm',
      'nodejs.org': 'Node.js',
      'vuejs.org': 'Vue.js',
      'reactjs.org': 'React'
    };
    
    if (domainMappings[hostname]) {
      console.log(`[generateNameFromUrl] 既知ドメイン: ${hostname} -> ${domainMappings[hostname]}`);
      return domainMappings[hostname];
    }
    
    // 一般的なドメインの場合は最初の文字を大文字にして返す
    const parts = hostname.split('.');
    if (parts.length > 1) {
      const mainDomain = parts[0];
      const capitalizedName = mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1);
      console.log(`[generateNameFromUrl] 一般ドメイン: ${hostname} -> ${capitalizedName}`);
      return capitalizedName;
    }
    
    return hostname;
  } catch (error) {
    console.error('[generateNameFromUrl] URL解析エラー:', error);
    return 'ブックマーク';
  }
}

/**
 * 画像URLが有効な画像を返すかテストする
 * @param {string} imageUrl 検証する画像URL
 * @param {number} timeout タイムアウト時間（ミリ秒）
 * @returns {Promise<boolean>} 有効な画像の場合true
 */
function testImageLoad(imageUrl, timeout = 5000) {
  return new Promise((resolve) => {
    const img = new Image();
    
    const timer = setTimeout(() => {
      console.log(`[testImageLoad] タイムアウト: ${imageUrl}`);
      resolve({ valid: false, width: 0, height: 0 });
    }, timeout);
    
    img.onload = () => {
      clearTimeout(timer);
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      console.log(`[testImageLoad] 有効画像確認 (${w}x${h}): ${imageUrl}`);
      resolve({ valid: true, width: w, height: h });
    };
    
    img.onerror = () => {
      clearTimeout(timer);
      console.log(`[testImageLoad] 読み込みエラー: ${imageUrl}`);
      resolve({ valid: false, width: 0, height: 0 });
    };
    
    img.src = imageUrl;
  });
}

/**
 * ファビコンを取得する（自前APIからbase64データURL取得）
 * サーバー側で画像を取得・base64化するため、ホットリンク保護等の影響を受けない
 * @param {string} url サイトのURL
 * @returns {Promise<string>} 有効なファビコンのURL（data:形式）またはデフォルトアイコン
 */
export async function getFaviconUrlWithFallback(url) {
  try {
    console.log(`[getFaviconUrlWithFallback] ファビコン取得開始: ${url}`);
    
    // Step 1: サーバーAPIからファビコンデータURLを取得（キャッシュ済み or 新規取得）
    let serverFavicon = null;
    
    if (_cachedFavicon) {
      // fetchSiteTitleで既にAPIから取得済み
      serverFavicon = _cachedFavicon;
      _cachedFavicon = null;
      console.log(`[getFaviconUrlWithFallback] キャッシュ済みファビコン使用`);
    } else if (!_faviconAlreadyFetched) {
      // fetchSiteTitleが呼ばれなかった場合（名前入力済み）、APIから直接取得
      try {
        console.log(`[getFaviconUrlWithFallback] APIからファビコン取得試行`);
        const apiUrl = `/api/get-title?url=${encodeURIComponent(url)}`;
        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          if (data && data.favicon) {
            serverFavicon = data.favicon;
            console.log(`[getFaviconUrlWithFallback] API取得ファビコン使用`);
          }
        }
      } catch (e) {
        console.log(`[getFaviconUrlWithFallback] API取得失敗: ${e.message}`);
      }
    }
    _faviconAlreadyFetched = false; // 次回用にリセット
    
    // Step 2: サーバー取得ファビコン（base64データURL）をブラウザで画像検証
    if (serverFavicon) {
      const { valid, width, height } = await testImageLoad(serverFavicon);
      if (valid) {
        if (width > 16 || height > 16) {
          // 十分なサイズのファビコンが得られた
          console.log(`[getFaviconUrlWithFallback] サーバーファビコン有効 (${width}x${height})`);
          return serverFavicon;
        }
        // 16x16のみ - より大きなサイズをGoogle Favicon APIで探す
        console.log(`[getFaviconUrlWithFallback] 16x16ファビコン取得、より大きいサイズを探す`);
        const largerFavicon = await tryGoogleFaviconLarger(url);
        if (largerFavicon) {
          console.log(`[getFaviconUrlWithFallback] Google APIでより大きいファビコン取得`);
          return largerFavicon;
        }
        // 大きいサイズが見つからなかった - 16x16を最後の手段として使用
        console.log(`[getFaviconUrlWithFallback] 16x16を最後の手段として使用`);
        return serverFavicon;
      }
      console.log(`[getFaviconUrlWithFallback] サーバーファビコン無効（読み込みエラー）`);
    }
    
    // サーバーAPIで取得できなかった場合はデフォルトアイコン
    console.log(`[getFaviconUrlWithFallback] デフォルトアイコンを使用`);
    return '/bookmark.svg';
    
  } catch (error) {
    console.error('[getFaviconUrlWithFallback] エラー:', error);
    return '/bookmark.svg';
  }
}

/**
 * Google Favicon API でより大きなサイズのファビコンを試みる
 * サーバーAPIが 16x16 しか取得できなかった場合の最後の手段
 * @param {string} url サイトのURL
 * @returns {Promise<string|null>} 有効なファビコンURL（>16px）またはnull
 */
async function tryGoogleFaviconLarger(url) {
  try {
    const domain = new URL(url).hostname;
    const googleUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
    const { valid, width, height } = await testImageLoad(googleUrl);
    if (valid && width > 16 && height > 16) {
      return googleUrl;
    }
  } catch (e) {
    console.log(`[tryGoogleFaviconLarger] 失敗: ${e.message}`);
  }
  return null;
}

/**
 * ブックマークデータをCloudflare KVに保存する
 * @param {object} data 保存するデータ
 * @param {string} pinCode 暗号化用のPINコード
 * @returns {Promise<object>} 保存結果（ユニークIDなど）
 */
export async function saveToCloudflare(data, pinCode) {
  try {
    // API連携用のエンドポイント（実際の環境に合わせて変更）
    const apiUrl = 'https://bkms.txt365.org/api/save';
    
    // PINコードを使ってデータを暗号化
    // 本番環境ではより強固な暗号化を行う必要があります
    const encryptedData = encryptData(JSON.stringify(data), pinCode);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        data: encryptedData,
        timestamp: new Date().toISOString()
      })
    });
    
    if (!response.ok) {
      throw new Error(`クラウド保存に失敗しました: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('クラウド保存エラー:', error);
    throw error;
  }
}

/**
 * Cloudflare KVからブックマークデータを取得する
 * @param {string} uniqueId データの一意識別子
 * @param {string} pinCode 復号化用のPINコード
 * @returns {Promise<object>} 取得したブックマークデータ
 */
export async function getFromCloudflare(uniqueId, pinCode) {
  try {
    // API連携用のエンドポイント（実際の環境に合わせて変更）
    const apiUrl = `https://bkms.txt365.org/api/get/${uniqueId}`;
    
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`クラウドデータの取得に失敗しました: ${response.status} ${response.statusText}`);
    }
    
    const encryptedData = await response.json();
    
    // PINコードを使ってデータを復号化
    // 本番環境ではより強固な復号化を行う必要があります
    const decryptedData = decryptData(encryptedData.data, pinCode);
    
    return JSON.parse(decryptedData);
  } catch (error) {
    console.error('クラウドデータ取得エラー:', error);
    throw error;
  }
}

/**
 * Cloudflare KVからデータを削除する
 * @param {string} uniqueId 削除するデータの一意識別子
 * @param {string} pinCode 認証用のPINコード
 * @returns {Promise<boolean>} 削除成功時はtrue
 */
export async function deleteFromCloudflare(uniqueId, pinCode) {
  try {
    // API連携用のエンドポイント（実際の環境に合わせて変更）
    const apiUrl = `https://bkms.txt365.org/api/delete/${uniqueId}`;
    
    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        pin: pinCode // PINコードを送信して認証
      })
    });
    
    if (!response.ok) {
      throw new Error(`クラウドデータの削除に失敗しました: ${response.status} ${response.statusText}`);
    }
    
    return true;
  } catch (error) {
    console.error('クラウドデータ削除エラー:', error);
    throw error;
  }
}

/**
 * データを暗号化する（モック関数）
 * 実際の実装では適切な暗号化アルゴリズムを使用すること
 * @param {string} data 暗号化するデータ（JSON文字列）
 * @param {string} pinCode 暗号化キー
 * @returns {string} 暗号化されたデータ
 */
function encryptData(data, pinCode) {
  // これはモック実装です。実際の実装ではCryptoJSなどのライブラリを使って
  // 適切に暗号化を行う必要があります。
  return btoa(data + '|' + pinCode);
}

/**
 * データを復号化する（モック関数）
 * 実際の実装では適切な復号化アルゴリズムを使用すること
 * @param {string} encryptedData 暗号化されたデータ
 * @param {string} pinCode 復号化キー
 * @returns {string} 復号化されたデータ
 */
function decryptData(encryptedData, pinCode) {
  // これはモック実装です。実際の実装ではCryptoJSなどのライブラリを使って
  // 適切に復号化を行う必要があります。
  const decoded = atob(encryptedData);
  const parts = decoded.split('|');
  
  if (parts.length < 2 || parts[1] !== pinCode) {
    throw new Error('PINコードが不正です');
  }
  
  return parts[0];
}

/**
 * ユニークIDからクラウドデータのメタ情報を取得する
 * @param {string} uniqueId データの一意識別子
 * @returns {Promise<object>} メタ情報（作成日時、最終アクセス日時など）
 */
export async function getCloudDataMetaInfo(uniqueId) {
  try {
    // API連携用のエンドポイント（実際の環境に合わせて変更）
    const apiUrl = `https://bkms.txt365.org/api/meta/${uniqueId}`;
    
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`メタ情報の取得に失敗しました: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('メタ情報取得エラー:', error);
    throw error;
  }
}

/**
 * クラウド保存用のユニークIDを生成する（暗号学的に安全な乱数を使用）
 * @returns {string} 一意のID
 */
export function generateUniqueId() {
  const bytes = new Uint8Array(7);
  crypto.getRandomValues(bytes);
  const random = Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').substring(0, 9);
  return random + Date.now().toString(36);
}